import { z } from "zod";
import { ContentJob, LocationContent, OrgType } from "./content.js";
import { ActivityType, AgentKind, AgentRole, AgentStatus, AssignmentReason, DemoApp, DemoStatus, EmulatorApp, ImportPlatform, ImportStatus, ProspectOutcome, ProspectSource, ProspectStage, SessionStatus, } from "./enums.js";
import { NormalizedShop, ShopSettings } from "./shop.js";
const objectId = z.string().regex(/^[a-f\d]{24}$/i, "invalid id");
const isoDate = z.string();
/* ---------- Agents ---------- */
/** Partner facts carried from MyManager at mint time and cached on the Agent. */
export const PartnerRef = z.object({
    partnerId: objectId,
    partnerAuthId: objectId.optional(),
    organizationId: objectId.optional(),
    referralCode: z.string().optional(),
    parentPartnerId: objectId.nullable().optional(),
    tier: z.string().nullable().optional(),
    /** MyManager contact id of the internal employee assigned to this partner. */
    assignedEmployeeId: objectId.nullable().optional(),
});
export const AgentDTO = z.object({
    id: objectId,
    kind: AgentKind,
    role: AgentRole,
    status: AgentStatus,
    name: z.string(),
    email: z.string(),
    avatarUrl: z.string().optional(),
    mmUserId: objectId.optional(),
    /** Internal agents: the MyManager organization this account acts in (null = super-admin, top level). */
    mmOrgId: objectId.nullable().optional(),
    mmOrgName: z.string().optional(),
    mmLocationName: z.string().optional(),
    /** Internal agents: their MyManager employee contact id (matches Partner.assignedEmployeeId). */
    mmEmployeeId: objectId.optional(),
    partner: PartnerRef.optional(),
    managerId: objectId.nullable().optional(),
    lastSeenAt: isoDate.optional(),
    createdAt: isoDate,
});
/**
 * What MyManager sends when it mints a studio token for one of its users.
 * Server-to-server, API-key authenticated — the only host that can describe an
 * agent is MyManager itself.
 */
export const MintTokenBody = z.object({
    kind: AgentKind,
    name: z.string().min(1),
    email: z.string().email(),
    avatarUrl: z.string().url().optional(),
    mmUserId: objectId.optional(),
    /**
     * The MyManager organization the account acts in. An internal studio account is
     * the PAIR (mmUserId, mmOrgId): the same person is a different account in each
     * org they administer. null = a super-admin at the top level.
     */
    mmOrgId: objectId.nullable().optional(),
    /** Display names of that organization and the account's location (rosters, reassign picker). */
    mmOrgName: z.string().optional(),
    mmLocationName: z.string().optional(),
    mmEmployeeId: objectId.optional(),
    /** MyManager's own userType; "super-admin" bootstraps studio superadmin on first login. */
    mmUserType: z.string().optional(),
    partner: PartnerRef.optional(),
});
export const UpdateAgentBody = z.object({
    role: AgentRole.optional(),
    status: AgentStatus.optional(),
    managerId: objectId.nullable().optional(),
});
/* ---------- Prospects ---------- */
export const ContactInfo = z.object({
    firstName: z.string().default(""),
    lastName: z.string().default(""),
    email: z.string().default(""),
    phone: z.string().default(""),
    title: z.string().default(""),
});
/**
 * One physical location of a multi-location prospect. MyManager models a
 * location as its own account under the organization (own menu, own POS), so
 * each entry here becomes one location user with its own shop on provisioning.
 */
export const ProspectLocation = z.object({
    id: z.string(),
    name: z.string().min(1),
    address: z
        .object({
        line1: z.string().default(""),
        city: z.string().default(""),
        state: z.string().default(""),
        postalCode: z.string().default(""),
        /** Geocoded once (Google Places); the MyManager org map pins the location with these. */
        lat: z.number().optional(),
        lng: z.number().optional(),
    })
        .default({}),
    phone: z.string().default(""),
    /** This location's own site / page (a franchise site usually has one per location). */
    website: z.string().default(""),
    pageUrl: z.string().default(""),
    rating: z.number().nullish(),
    /** Where this location's menu comes from (a marketplace store page, usually). */
    source: z.object({ platform: ImportPlatform, url: z.string() }).nullable().default(null),
    status: z.enum(["pending", "importing", "imported", "failed"]).default("pending"),
    importJobId: z.string().nullable().default(null),
    itemCount: z.number().int().default(0),
    error: z.string().optional(),
    /** This location's own events / memberships / classes / services; null = use the prospect's shared set. */
    content: LocationContent.nullable().default(null),
});
export const ProspectDTO = z.object({
    id: objectId,
    businessName: z.string(),
    businessType: z.string().default("restaurant"),
    cuisine: z.string().default(""),
    website: z.string().default(""),
    builtSiteUrl: z.string().optional(),
    locationsCount: z.number().int().min(1).default(1),
    city: z.string().default(""),
    state: z.string().default(""),
    contact: ContactInfo,
    logoUrl: z.string().optional(),
    heroImageUrl: z.string().optional(),
    brandColor: z.string().optional(),
    /** Second brand colour + the typefaces their site uses (see importers/brand.ts). */
    brandSecondaryColor: z.string().optional(),
    brandHeadingFont: z.string().optional(),
    brandBodyFont: z.string().optional(),
    /** Settings the demo ShopV2 is created with (see shop.ts ShopSettings); null = MyManager defaults. */
    shopSettings: ShopSettings.nullable().default(null),
    /** organization | franchisor | auto (franchisor when 2+ locations). */
    orgType: OrgType.default("auto"),
    /** Shared events / memberships / classes / services, pushed to every location without its own. */
    content: LocationContent.nullable().default(null),
    contentJob: ContentJob.nullable().default(null),
    source: ProspectSource,
    sourceUrl: z.string().optional(),
    stage: ProspectStage,
    outcome: ProspectOutcome,
    value: z.number().min(0).default(0),
    ownerAgentId: objectId,
    sourcedByAgentId: objectId,
    closedByAgentId: objectId.nullable().optional(),
    assignedAt: isoDate,
    lastActivityAt: isoDate,
    slaDeadlineAt: isoDate.nullable().optional(),
    convertedOrgId: z.string().nullable().optional(),
    mmLeadId: z.string().nullable().optional(),
    /** Note entered when the prospect was marked won / lost / missed (any outcome). */
    lostReason: z.string().optional(),
    /** When the outcome was set (won / lost / missed); null while open. */
    closedAt: isoDate.nullable().optional(),
    tags: z.array(z.string()).default([]),
    /** Empty = single-location business (the prospect's own shop). */
    locations: z.array(ProspectLocation).default([]),
    /** Denormalized for list views. */
    activeDemo: z
        .object({
        id: objectId,
        status: DemoStatus,
        createdAt: isoDate.optional(),
        expiresAt: isoDate,
        shareToken: z.string(),
        posPin: z.string().nullable().optional(),
    })
        .nullable()
        .optional(),
    createdAt: isoDate,
    updatedAt: isoDate,
});
export const CreateProspectBody = z.object({
    businessName: z.string().min(1),
    businessType: z.string().default("restaurant"),
    cuisine: z.string().optional(),
    website: z.string().optional(),
    /** The site WE built for them (webbuilder / custom domain); shown as "Your site" in the portal. */
    builtSiteUrl: z.string().optional(),
    locationsCount: z.number().int().min(1).optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    contact: ContactInfo.partial().optional(),
    source: ProspectSource.default("manual"),
    sourceUrl: z.string().optional(),
    value: z.number().min(0).optional(),
    tags: z.array(z.string()).optional(),
    /** Manager/superadmin may create on behalf of another agent. */
    ownerAgentId: objectId.optional(),
    orgType: OrgType.optional(),
    /** Multi-location: one entry per location to build; each may name its menu source. */
    locations: z
        .array(z.object({
        name: z.string().min(1),
        address: ProspectLocation.shape.address.optional(),
        phone: z.string().optional(),
        website: z.string().optional(),
        pageUrl: z.string().optional(),
        source: z.object({ platform: ImportPlatform, url: z.string() }).nullable().optional(),
    }))
        .optional(),
});
export const UpdateProspectBody = CreateProspectBody.partial().extend({
    stage: ProspectStage.optional(),
    logoUrl: z.string().optional(),
    heroImageUrl: z.string().optional(),
    brandColor: z.string().optional(),
    /** Second brand colour + the typefaces their site uses (see importers/brand.ts). */
    brandSecondaryColor: z.string().optional(),
    brandHeadingFont: z.string().optional(),
    brandBodyFont: z.string().optional(),
    shopSettings: ShopSettings.nullable().optional(),
    content: LocationContent.nullable().optional(),
});
/** Read events / memberships / classes / services off their site (+ web), or generate samples. */
export const ContentDiscoverBody = z.object({
    /** "shared" = the prospect-level set; "all" = shared + every location with its own page; or one location id. */
    scope: z.string().default("all"),
    mode: z.enum(["discover", "sample"]).default("discover"),
});
export const SetContentBody = z.object({
    /** null/absent = the shared set. */
    locationId: z.string().nullable().optional(),
    content: LocationContent.nullable(),
});
export const SetOutcomeBody = z.object({
    outcome: ProspectOutcome,
    reason: z.string().optional(),
    value: z.number().min(0).optional(),
});
export const AssignProspectBody = z.object({
    toAgentId: objectId,
    reason: AssignmentReason.default("manual"),
    note: z.string().optional(),
});
/* ---------- Demos ---------- */
/** A registered screen device in the demo shop; the native app pairs with `code`. */
export const DemoDeviceType = z.enum(["POS", "KDS", "CFD", "Self Ordering Kiosk"]);
/** What the screen is for; two POS-type devices exist per shop (register + handheld). */
export const DemoDeviceRole = z.enum(["register", "handheld", "cfd", "kds", "kiosk"]);
export const DemoDevice = z.object({
    shopId: z.string(),
    /** Location label (the studio's location name when known, else the shop name). */
    location: z.string(),
    type: DemoDeviceType,
    role: DemoDeviceRole.optional(),
    name: z.string(),
    code: z.string(),
});
export const DemoDTO = z.object({
    id: objectId,
    prospectId: objectId,
    status: DemoStatus,
    apps: z.array(DemoApp),
    /** Org + owner user inside the mymanager-demo database. */
    demoOrgId: z.string().nullable().optional(),
    demoUserId: z.string().nullable().optional(),
    demoLoginEmail: z.string().nullable().optional(),
    demoLoginPassword: z.string().nullable().optional(),
    /** Where a magic-link login lands (mm-client-demo). */
    adminUrl: z.string().nullable().optional(),
    websiteUrl: z.string().nullable().optional(),
    /** The primary ShopV2 online store (`/online-shop/<shopId>` on the demo client). */
    shopId: z.string().nullable().optional(),
    onlineShopUrl: z.string().nullable().optional(),
    /** Employee PIN the POS asks for after pairing. */
    posPin: z.string().nullable().optional(),
    /** One pairing code per screen role (POS, KDS, CFD, kiosk) per location. */
    devices: z.array(DemoDevice).default([]),
    shareToken: z.string(),
    shareUrl: z.string(),
    activatedAt: isoDate.nullable().optional(),
    expiresAt: isoDate,
    extendedCount: z.number().int().default(0),
    lastOpenedAt: isoDate.nullable().optional(),
    openCount: z.number().int().default(0),
    error: z.string().optional(),
    /** Provisioned locations (multi-location demos): MyManager user + shop per location. */
    locations: z
        .array(z.object({
        name: z.string(),
        demoUserId: z.string(),
        shopId: z.string().nullable().optional(),
        shopPath: z.string().nullable().optional(),
    }))
        .default([]),
    createdAt: isoDate,
});
export const CreateDemoBody = z.object({
    prospectId: objectId,
    apps: z.array(DemoApp).default(["admin", "client", "pos", "website"]),
    /** Days until expiry; capped by settings. */
    days: z.number().int().min(1).max(30).optional(),
    /** Pre-fill the demo org from a reviewed import. */
    importJobId: objectId.optional(),
});
export const ExtendDemoBody = z.object({ days: z.number().int().min(1).max(30) });
/* ---------- Presentations ---------- */
export const PresentationSlide = z.object({
    id: z.string(),
    kind: z.enum([
        "why_mymanager",
        "challenges",
        "solutions",
        "benefits",
        "growth",
        "next_steps",
        "closing",
        "custom",
    ]),
    title: z.string(),
    subtitle: z.string().default(""),
    imageUrl: z.string().optional(),
    quote: z.string().optional(),
    quoteBy: z.string().optional(),
    points: z
        .array(z.object({ title: z.string(), detail: z.string().default(""), icon: z.string().optional() }))
        .default([]),
});
/** One talking point from the website audit: a gap to fix, something that's fine, or context. */
export const SiteFinding = z.object({
    key: z.string(),
    severity: z.enum(["gap", "ok", "info"]),
    title: z.string(),
    detail: z.string(),
});
/** What the prospect's website offers today (read by the audit, feeds the deck). */
export const SiteAudit = z.object({
    url: z.string(),
    fetchedAt: z.string(),
    reachable: z.boolean(),
    title: z.string().default(""),
    description: z.string().default(""),
    builder: z.string().default(""),
    https: z.boolean(),
    mobileViewport: z.boolean(),
    pageKb: z.number(),
    scripts: z.number(),
    pagesChecked: z.number(),
    menuOnSite: z.boolean(),
    menuAsPdf: z.boolean(),
    ordering: z.object({
        own: z.boolean(),
        provider: z.string().nullable(),
        marketplaces: z.array(z.string()),
    }),
    reservations: z.object({ has: z.boolean(), provider: z.string().nullable() }),
    booking: z.object({ has: z.boolean(), provider: z.string().nullable() }),
    loyalty: z.boolean(),
    giftCards: z.boolean(),
    catering: z.boolean(),
    events: z.boolean(),
    memberships: z.boolean(),
    mobileApp: z.object({ ios: z.boolean(), android: z.boolean() }),
    social: z.array(z.string()),
    contact: z.object({
        phone: z.boolean(),
        address: z.boolean(),
        hours: z.boolean(),
        form: z.boolean(),
        email: z.boolean(),
        newsletter: z.boolean(),
    }),
    locations: z.number(),
    copyrightYear: z.number().nullable(),
    findings: z.array(SiteFinding),
});
/** One thing the web (reviews, listings, socials) says about the business. */
export const ResearchProblem = z.object({
    title: z.string(),
    detail: z.string(),
    /** Where it came from: "reviews", "listings", "social", "website", "ordering", "reputation"… */
    area: z.string().default(""),
    /** URL(s) that back it up, when the model cited one of the search results. */
    sources: z.array(z.string()).default([]),
});
/** A search result about the business, classified. */
export const WebPresence = z.object({
    site: z.string(),
    kind: z.enum(["reviews", "listing", "ordering", "social", "booking", "news", "other"]),
    url: z.string(),
    title: z.string().default(""),
    snippet: z.string().default(""),
});
/** "Search all of them": what the wider web says, summarised by the model. */
export const WebResearch = z.object({
    fetchedAt: z.string(),
    queries: z.array(z.string()),
    presence: z.array(WebPresence),
    /** Model-written: every problem we can see for the business as a whole, gaps first. */
    problems: z.array(ResearchProblem),
    strengths: z.array(ResearchProblem),
    summary: z.string().default(""),
    /** How the summary was produced. */
    by: z.enum(["ai", "rules"]).default("rules"),
});
export const PresentationDTO = z.object({
    id: objectId,
    prospectId: objectId,
    slides: z.array(PresentationSlide),
    /** How the current slides came to be: written by a model, built from the audit by rules, or the stock deck. */
    generatedBy: z.enum(["ai", "rules", "default"]).default("default"),
    generatedAt: isoDate.nullable().default(null),
    audit: SiteAudit.nullable().default(null),
    research: WebResearch.nullable().default(null),
    updatedAt: isoDate,
});
export const UpsertPresentationBody = z.object({ slides: z.array(PresentationSlide).min(1) });
/** Set one slide's image: a URL, or have the image model draw one (optional prompt). */
export const SlideImageBody = z.object({
    url: z.string().url().optional(),
    generate: z.boolean().optional(),
    prompt: z.string().max(600).optional(),
    remove: z.boolean().optional(),
});
/** Generate photos for imported items that have none. */
export const GenerateImagesBody = z.object({
    /** Max items to draw in this run (cost guard). */
    limit: z.number().int().min(1).max(200).default(40),
});
/* ---------- Discovery ("find this business online") ---------- */
export const DiscoverBody = z.object({
    businessName: z.string().min(2),
    city: z.string().optional(),
    state: z.string().optional(),
    website: z.string().optional(),
});
export const DiscoveredSource = z.object({
    id: z.string(),
    platform: z.union([ImportPlatform, z.literal("other")]),
    label: z.string(),
    title: z.string(),
    url: z.string(),
    detail: z.string(),
    imageUrl: z.string().optional(),
    /** Google may know their website even when the agent didn't type one. */
    website: z.string().optional(),
    reliability: z.enum(["api", "best_effort", "info"]),
    importable: z.boolean(),
    via: z.enum(["website", "google", "search"]),
    rank: z.number().optional(),
    /** Marketplace store pages are per location; the label parsed from the store slug ("Bayside"). */
    location: z.string().optional(),
    /** The platform's own store id, so the same store under two slugs is one source. */
    storeId: z.string().optional(),
});
/** One real location and every source that carries it. */
export const DiscoveredLocation = z.object({
    key: z.string(),
    name: z.string(),
    address: ProspectLocation.shape.address,
    phone: z.string().default(""),
    /** Sorted by platform preference; `importable` says whether a menu can be read from it. */
    sources: z.array(z.object({
        sourceId: z.string(),
        platform: z.union([ImportPlatform, z.literal("other")]),
        label: z.string(),
        url: z.string(),
        importable: z.boolean(),
    })),
});
export const DiscoverResult = z.object({
    sources: z.array(DiscoveredSource),
    /** The website's own location list first (with addresses), then locations only other sources know. */
    locations: z.array(DiscoveredLocation).default([]),
});
/** Business details of ONE source, to pre-fill the form when the agent selects it. */
export const DiscoverDetailsBody = z.object({
    platform: ImportPlatform,
    url: z.string().optional(),
    businessName: z.string().min(1),
    city: z.string().optional(),
    state: z.string().optional(),
});
export const DiscoverDetailsResult = z.object({
    business: z
        .object({
        name: z.string(),
        cuisine: z.string(),
        description: z.string(),
        phone: z.string(),
        email: z.string(),
        website: z.string(),
        address: z.object({
            line1: z.string(),
            city: z.string(),
            state: z.string(),
            postalCode: z.string(),
        }),
        logoUrl: z.string().optional(),
        heroImageUrl: z.string().optional(),
        brandColor: z.string().optional(),
        brandSecondaryColor: z.string().optional(),
        headingFont: z.string().optional(),
        bodyFont: z.string().optional(),
        rating: z.number().optional(),
        reviewCount: z.number().optional(),
    })
        .nullable(),
    items: z.number(),
    images: z.number(),
    hours: z.number(),
    error: z.string().optional(),
});
/* ---------- Imports ---------- */
export const ImportJobDTO = z.object({
    id: objectId,
    prospectId: objectId,
    platform: ImportPlatform,
    locationId: z.string().nullable().optional(),
    sourceUrl: z.string().optional(),
    status: ImportStatus,
    progress: z.number().min(0).max(100).default(0),
    message: z.string().default(""),
    shop: NormalizedShop.optional(),
    error: z.string().optional(),
    appliedAt: isoDate.nullable().optional(),
    /** Photo generation for items without one (optional, OpenAI Images). */
    imageGen: z
        .object({
        status: z.enum(["running", "done", "failed"]),
        total: z.number().int(),
        done: z.number().int(),
        failed: z.number().int().default(0),
        message: z.string().default(""),
    })
        .nullable()
        .optional(),
    createdAt: isoDate,
});
export const CreateImportBody = z.object({
    prospectId: objectId,
    platform: ImportPlatform,
    sourceUrl: z.string().optional(),
    /** Multi-location: which prospect location this import feeds. */
    locationId: z.string().optional(),
    /** menu_pdf: an uploaded file reference or a URL. */
    fileUrl: z.string().optional(),
});
export const ReviewImportBody = z.object({ shop: NormalizedShop });
/** Merge several finished imports of one prospect into one shop (richest menu + brand + hours). */
export const MergeImportsBody = z.object({
    prospectId: objectId,
    jobIds: z.array(objectId).min(1).max(8),
});
export const MergeSourceReport = z.object({
    platform: z.string(),
    items: z.number(),
    images: z.number(),
    base: z.boolean(),
    itemsAdded: z.number(),
    imagesFilled: z.number(),
    descriptionsFilled: z.number(),
    modifiersFilled: z.number(),
    variantsFilled: z.number().default(0),
    pricesFilled: z.number(),
    fields: z.array(z.string()),
});
export const MergeReport = z.object({
    base: z.string(),
    sources: z.array(MergeSourceReport),
    totals: z.object({
        items: z.number(),
        withImages: z.number(),
        withDescriptions: z.number(),
        categories: z.number(),
        images: z.number(),
    }),
});
export const MergePreviewDTO = z.object({ shop: NormalizedShop, report: MergeReport });
/* ---------- Activity / Assignment ---------- */
export const ActivityDTO = z.object({
    id: objectId,
    prospectId: objectId,
    demoId: objectId.nullable().optional(),
    agentId: objectId.nullable().optional(),
    type: ActivityType,
    summary: z.string(),
    data: z.record(z.unknown()).default({}),
    at: isoDate,
});
export const LogActivityBody = z.object({
    type: z.enum(["call_logged", "note_added"]),
    summary: z.string().min(1),
    data: z.record(z.unknown()).optional(),
});
export const AssignmentDTO = z.object({
    id: objectId,
    prospectId: objectId,
    fromAgentId: objectId.nullable(),
    toAgentId: objectId,
    reason: AssignmentReason,
    note: z.string().optional(),
    byAgentId: objectId.nullable(),
    at: isoDate,
});
/* ---------- Emulator sessions ---------- */
/** Display the emulator presents: landscape tablet (POS), phone (1080x2400), portrait tablet (kiosk). */
export const FormFactor = z.enum(["tablet", "phone", "tablet-portrait"]);
export const EmulatorSessionDTO = z.object({
    id: objectId,
    demoId: objectId,
    agentId: objectId,
    app: EmulatorApp,
    status: SessionStatus,
    /** Which frame on the page owns this session ("phone", "pos", "hw-kiosk"…); null for old rows. */
    purpose: z.string().nullable().optional(),
    formFactor: FormFactor.nullable().optional(),
    /** ShopV2 device code the POS paired with (hardware roles), when one was requested. */
    deviceCode: z.string().nullable().optional(),
    /** Logical id of the emulator's customer display when the session was started with `cfd` (streamed at /cfd/<slot>/). */
    cfdDisplayId: z.number().int().nullable().optional(),
    /** Same-origin path the web app connects to; the api proxies it to the broker. */
    streamPath: z.string().nullable(),
    /** adb serial of the emulator behind streamPath (ws-scrcpy's `udid`). */
    streamUdid: z.string().nullable().optional(),
    slot: z.string().nullable(),
    startedAt: isoDate,
    expiresAt: isoDate,
    endedAt: isoDate.nullable().optional(),
    error: z.string().optional(),
});
export const CreateSessionBody = z.object({
    demoId: objectId,
    app: EmulatorApp,
    purpose: z.string().max(40).optional(),
    /** Pair the POS with this registered device instead of a fresh emulator device. */
    deviceCode: z.string().max(12).optional(),
    formFactor: FormFactor.optional(),
    /** Register only: add a customer-facing second display to the same emulator. */
    cfd: z.boolean().optional(),
});
/* ---------- Portal (public, share-token scoped) ---------- */
export const PortalDTO = z.object({
    prospect: z.object({
        businessName: z.string(),
        cuisine: z.string(),
        city: z.string(),
        state: z.string(),
        website: z.string(),
        /** The site we built for them; the portal's "Your site" view embeds it when set. */
        builtSiteUrl: z.string().optional(),
        locationsCount: z.number(),
        address: z.string(),
        phone: z.string(),
        contact: ContactInfo,
        logoUrl: z.string().optional(),
        heroImageUrl: z.string().optional(),
        brandColor: z.string().optional(),
        /** Second brand colour + the typefaces their site uses (see importers/brand.ts). */
        brandSecondaryColor: z.string().optional(),
        brandHeadingFont: z.string().optional(),
        brandBodyFont: z.string().optional(),
    }),
    demo: z.object({
        id: objectId,
        status: DemoStatus,
        apps: z.array(DemoApp),
        expiresAt: isoDate,
        adminUrl: z.string().nullable().optional(),
        websiteUrl: z.string().nullable().optional(),
        /** Their live online store inside the demo (ShopV2), when the demo is active. */
        onlineShopUrl: z.string().nullable().optional(),
        /** The prospect's own demo login and the POS employee PIN (active demos only). */
        loginEmail: z.string().nullable().optional(),
        loginPassword: z.string().nullable().optional(),
        posPin: z.string().nullable().optional(),
        devices: z.array(DemoDevice).default([]),
    }),
    agent: z.object({ name: z.string(), email: z.string(), avatarUrl: z.string().optional() }),
    presentation: z.array(PresentationSlide),
    shop: NormalizedShop.optional(),
    /** All locations of a multi-location demo (name, address, phone, menu size). */
    locations: z
        .array(z.object({
        name: z.string(),
        address: z.string(),
        phone: z.string(),
        itemCount: z.number().int(),
        shop: NormalizedShop.optional(),
        /** This location's own online store in the demo, when provisioned. */
        onlineShopUrl: z.string().nullable().optional(),
    }))
        .default([]),
});
export const PortalCustomerInfoBody = z.object({
    businessName: z.string().min(1).optional(),
    cuisine: z.string().optional(),
    website: z.string().optional(),
    locationsCount: z.number().int().min(1).optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    address: z.string().optional(),
    contact: ContactInfo.partial().optional(),
});
export const PortalEventBody = z.object({
    type: z.enum(["link_opened", "portal_section_viewed"]),
    section: z.string().optional(),
});
/* ---------- Settings / metrics ---------- */
export const SettingsDTO = z.object({
    demoDefaultDays: z.number().int().min(1).max(30),
    demoMaxDays: z.number().int().min(1).max(60),
    slaInactivityDays: z.number().int().min(1),
    slaHardCapDays: z.number().int().min(1),
    slaWarningDays: z.number().int().min(0),
    partnerMaxConcurrentSessions: z.number().int().min(1),
    sessionMaxMinutes: z.number().int().min(5),
    sessionIdleMinutes: z.number().int().min(1),
    /** Where SLA-expired partner prospects go when the partner has no assigned employee. */
    internalQueueAgentId: z.string().nullable(),
});
export const DashboardMetricsDTO = z.object({
    range: z.object({ from: isoDate, to: isoDate }),
    prospects: z.object({
        created: z.number(),
        open: z.number(),
        won: z.number(),
        lost: z.number(),
        missed: z.number(),
        winRate: z.number(),
    }),
    demos: z.object({
        created: z.number(),
        opened: z.number(),
        openRate: z.number(),
        activeNow: z.number(),
        expiringSoon: z.number(),
    }),
    sessions: z.object({ count: z.number(), minutes: z.number() }),
});
export const Paginated = (item) => z.object({ items: z.array(item), total: z.number(), page: z.number(), pageSize: z.number() });
//# sourceMappingURL=dto.js.map